import { z } from 'zod';
import { FIELD_TYPE_CATALOGUE, filterOperatorSchema, } from './fields.js';
/**
 * The shared filter language (RELAY-940): what every list endpoint accepts in `filter`.
 *
 *   { "and": [ { "field": "stage", "op": "in", "value": ["Screening", "Interview"] },
 *              { "or": [ { "field": "country", "op": "eq", "value": "Colombia" },
 *                        { "field": "expectedHourlyRate", "op": "lte", "value": 12 } ] } ] }
 *
 * A condition's operator must be one the field's TYPE supports (`FIELD_TYPE_CATALOGUE`), and
 * its value must parse as a value of that type — `validateFilter()` checks both against an
 * entity's field catalogue, so a client cannot send an operator the storage layer has no
 * translation for, and the API answers 400 with the path of the offending condition.
 */
/**
 * The longest field name a filter, sort or projection may carry: `custom.<key>`, a key being at
 * most 64 characters (`fieldKeySchema`). 64 alone refused sorting and filtering on the longest
 * valid custom fields.
 */
export const FIELD_NAME_MAX = 'custom.'.length + 64;
export const filterConditionSchema = z.object({
    field: z.string().min(1).max(FIELD_NAME_MAX),
    op: filterOperatorSchema,
    value: z.unknown().optional(),
});
export const MAX_FILTER_DEPTH = 5;
export const MAX_FILTER_BRANCHES = 50;
// `id` registers the recursive schema in zod's global registry, so the OpenAPI document emits it
// ONCE under components.schemas.Filter and refers to it by name — a self-reference inlined under
// a path is a $ref that openapi-typescript (the web client generator) cannot resolve.
export const filterSchema = z
    .lazy(() => z.union([
    filterConditionSchema,
    z.object({ and: z.array(filterSchema).min(1).max(MAX_FILTER_BRANCHES) }),
    z.object({ or: z.array(filterSchema).min(1).max(MAX_FILTER_BRANCHES) }),
]))
    .meta({ id: 'Filter', description: 'A condition, or and/or over nested filters' });
export const sortSchema = z.object({
    field: z.string().min(1).max(FIELD_NAME_MAX),
    dir: z.enum(['asc', 'desc']).default('asc'),
});
/** The body of `POST /<entity>:search`; `GET /<entity>` accepts the same keys as query parameters. */
export const searchRequestSchema = z.object({
    /** Free text: full-text + fuzzy match over the entity's searchable text (names, emails, title). */
    q: z.string().trim().min(1).max(200).optional(),
    filter: filterSchema.optional(),
    /** One key: pages are keyset-paged on (sort key, id), so a second key would not be stable. */
    sort: z.array(sortSchema).max(1).optional(),
    /** Project the response to these fields (id is always included). */
    fields: z.array(z.string().min(1).max(FIELD_NAME_MAX)).max(60).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(200).default(50),
});
const NO_VALUE_OPS = ['is_empty', 'is_not_empty'];
const LIST_OPS = ['in', 'not_in'];
/**
 * Validate a filter against an entity's field catalogue (`{ fieldName: FieldType }`).
 * Returns the problems (empty = valid): unknown field, operator the type does not support,
 * value of the wrong shape, or nesting past MAX_FILTER_DEPTH.
 */
export function validateFilter(filter, fields, path = 'filter', depth = 0) {
    if (depth > MAX_FILTER_DEPTH)
        return [{ path, message: `nested deeper than ${MAX_FILTER_DEPTH}` }];
    if ('and' in filter)
        return filter.and.flatMap((f, i) => validateFilter(f, fields, `${path}.and[${i}]`, depth + 1));
    if ('or' in filter)
        return filter.or.flatMap((f, i) => validateFilter(f, fields, `${path}.or[${i}]`, depth + 1));
    const type = fields[filter.field];
    if (!type)
        return [{ path: `${path}.field`, message: `unknown field '${filter.field}'` }];
    const spec = FIELD_TYPE_CATALOGUE[type];
    if (!spec.operators.includes(filter.op))
        return [{ path: `${path}.op`, message: `'${filter.op}' is not valid for a ${type} field` }];
    if (NO_VALUE_OPS.includes(filter.op)) {
        return filter.value === undefined
            ? []
            : [{ path: `${path}.value`, message: `'${filter.op}' takes no value` }];
    }
    if (filter.value === undefined)
        return [{ path: `${path}.value`, message: `'${filter.op}' needs a value` }];
    // A multi-select's own value is already an array; `in`/`not_in` on anything else takes a list of single values.
    const valueSchema = LIST_OPS.includes(filter.op) && type !== 'multi_select'
        ? z.array(spec.value).min(1).max(500)
        : spec.value;
    const parsed = valueSchema.safeParse(filter.value);
    return parsed.success
        ? []
        : [{ path: `${path}.value`, message: parsed.error.issues[0]?.message ?? 'invalid value' }];
}
/**
 * A condition the API can run: its operator takes no value, or it has one (not `''`, not `[]`).
 * A filter row the user added but has not filled yet is incomplete.
 */
export function isConditionComplete(c) {
    if (NO_VALUE_OPS.includes(c.op))
        return true;
    if (c.value === undefined || c.value === null)
        return false;
    if (typeof c.value === 'string')
        return c.value.trim() !== '';
    return !Array.isArray(c.value) || c.value.length > 0;
}
/** The filter without its incomplete conditions — what a list sends; `undefined` when nothing is left. */
export function completeFilter(filter) {
    if (!filter)
        return undefined;
    if ('field' in filter)
        return isConditionComplete(filter) ? filter : undefined;
    const parts = ('and' in filter ? filter.and : filter.or)
        .map(completeFilter)
        .filter((f) => f !== undefined);
    if (parts.length === 0)
        return undefined;
    return 'and' in filter ? { and: parts } : { or: parts };
}
/** Every field a filter or sort may name, with the issue for the first unknown one. */
export function validateSort(sort, sortable) {
    return (sort ?? [])
        .filter((s) => !sortable.includes(s.field))
        .map((s) => ({ path: 'sort', message: `'${s.field}' is not sortable` }));
}
//# sourceMappingURL=filters.js.map